import {objectFromEntries} from '@benchling/js-utils';
import {createStyles, Text} from '@mantine/core';
import React from 'react';

import getFieldIcon from 'analysis-tool/structured-data-mapping/column-mapping-table/getFieldIcon';
import {MappableLayer, ScratchPlateColumnMapping} from 'plates/dao/ScratchPlateDAO';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ScratchPlateSourceColumnSelector, {
  decodeSourceColumn,
  encodeSourceColumn,
} from './ScratchPlateSourceColumnSelector';

export interface ScratchPlateColumnMappingTableProps {
  /** Container-schema fields that can be set (the mapping destinations). */
  schemaFields: Array<SchemaField>;
  /** Source columns, grouped by scratch-plate layer. */
  layers: Array<MappableLayer>;
  mappings: Array<ScratchPlateColumnMapping>;
  setMappings: (mappings: Array<ScratchPlateColumnMapping>) => void;
}

function updateMappingsOnSelect(
  mappings: Array<ScratchPlateColumnMapping>,
  schemaFieldName: string,
  token: string | null
): Array<ScratchPlateColumnMapping> {
  const withoutField = mappings.filter((m) => m.schemaFieldName !== schemaFieldName);
  if (token == null) {
    return withoutField;
  }
  const {layerName, columnName} = decodeSourceColumn(token);
  return [...withoutField, {layerName, columnName, schemaFieldName}];
}

/**
 * Destination-centric column mapping, mirroring the analysis tool's "map to
 * results" table (`ColumnMappingTable`): one row per container-schema field —
 * shown with its type icon — each paired with a dropdown of scratch-plate
 * source columns grouped by layer. Required fields are marked with an asterisk.
 *
 * Source columns are layer-qualified so that identical column names in
 * different layers stay distinct.
 */
export default function ScratchPlateColumnMappingTable({
  schemaFields,
  layers,
  mappings,
  setMappings,
}: ScratchPlateColumnMappingTableProps): JSX.Element {
  const {classes} = useStyles();

  const selectedTokenByField = objectFromEntries(
    mappings.map((m) => [m.schemaFieldName, encodeSourceColumn(m.layerName, m.columnName)])
  );

  if (schemaFields.length === 0) {
    return <Text c="dimmed">This schema does not have any fields to map.</Text>;
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Plate column</th>
          <th>Source column</th>
        </tr>
      </thead>
      <tbody>
        {schemaFields.map((schemaField) => (
          <tr key={schemaField.systemName}>
            <td className={classes.fieldCell}>
              <BenchlingIcon className={classes.typeIcon} icon={getFieldIcon(schemaField)} />
              {schemaField.displayName}
              {schemaField.isRequired && (
                <Text span={true} c="red.6">
                  {' '}
                  *
                </Text>
              )}
            </td>
            <td className={classes.selectorCell}>
              <ScratchPlateSourceColumnSelector
                layers={layers}
                selectedValue={selectedTokenByField[schemaField.systemName] ?? null}
                onChange={(token): void => {
                  setMappings(updateMappingsOnSelect(mappings, schemaField.systemName, token));
                }}
                allowSelectNull={!schemaField.isRequired}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const useStyles = createStyles((theme) => ({
  fieldCell: {
    // Align the shorter field label with the taller dropdown selector.
    verticalAlign: 'middle',
  },
  typeIcon: {
    color: theme.colors.gray[6],
    marginRight: theme.spacing.xs,
  },
  selectorCell: {
    // Fix the width so it does not jump as different options are selected.
    width: 255,
  },
}));
